import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { getStatusBadgeStyle, formatDate } from 'utils/lmmsHelpers';
import { SyutSelect } from 'components/ui';

// ============================================================================
// Types
// ============================================================================

export interface BusinessUnitFormData {
  entityId: string;
  buCode: string;
  buName: string;
  buType: string;
  description: string;
}

export interface BusinessUnitRecord {
  id: string;
  entityId: string;
  entityName: string;
  buCode: string;
  buName: string;
  buType: string;
  parentBuId?: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  createdBy?: string;
  createdByName?: string;
  updatedAt?: string;
  updatedByName?: string;
  rowVersion: number;
}

interface EntityOption {
  value: string;
  label: string;
}

interface BusinessUnitFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  formData: BusinessUnitFormData;
  entityOptions: EntityOption[];
  onFormDataChange: (data: BusinessUnitFormData) => void;
  onSave: () => void;
  onClose: () => void;
}

interface BusinessUnitViewModalProps {
  isOpen: boolean;
  record: BusinessUnitRecord | null;
  onClose: () => void;
}

const BU_TYPE_OPTIONS = [
  { value: 'BRANCH', label: 'Branch' },
  { value: 'HEAD_OFFICE', label: 'Head Office' },
  { value: 'REGIONAL_OFFICE', label: 'Regional Office' },
];

// ============================================================================
// Form Modal (Create / Edit)
// ============================================================================

export const BusinessUnitFormModal: React.FC<BusinessUnitFormModalProps> = ({
  isOpen,
  mode,
  formData,
  entityOptions,
  onFormDataChange,
  onSave,
  onClose,
}) => {
  if (!isOpen) return null;

  const title = mode === 'create' ? 'Add New Branch' : 'Edit Branch';

  const handleChange = (field: keyof BusinessUnitFormData, value: string) => {
    onFormDataChange({ ...formData, [field]: value });
  };

  const isValid =
    formData.entityId.trim() !== '' &&
    formData.buCode.trim() !== '' &&
    formData.buName.trim() !== '';

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-sm">
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div className="form-group">
            <SyutSelect
              label="Entity (Company) *"
              options={entityOptions}
              value={formData.entityId}
              onChange={(val: string | string[] | null) => handleChange('entityId', (val as string) || '')}
              placeholder="Select entity..."
              isClearable={false}
              isSearchable={true}
              size="sm"
            />
          </div>

          <div className="form-group">
            <label className="form-label">BU Code *</label>
            <input
              type="text"
              className="form-input"
              value={formData.buCode}
              onChange={(e) => handleChange('buCode', e.target.value)}
              placeholder="e.g., BR-001"
              maxLength={20}
              style={{ border: '1px solid #334155' }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">BU Name *</label>
            <input
              type="text"
              className="form-input"
              value={formData.buName}
              onChange={(e) => handleChange('buName', e.target.value)}
              placeholder="e.g., Main Branch"
              maxLength={100}
              style={{ border: '1px solid #334155' }}
            />
          </div>

          <div className="form-group">
            <SyutSelect
              label="BU Type *"
              options={BU_TYPE_OPTIONS}
              value={formData.buType}
              onChange={(val: string | string[] | null) => handleChange('buType', (val as string) || 'BRANCH')}
              placeholder="Select type..."
              isClearable={false}
              size="sm"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-input"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Optional description"
              rows={3}
              maxLength={500}
              style={{ border: '1px solid #334155', resize: 'vertical' }}
            />
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={onSave} disabled={!isValid}>
            <IconSystem.save size={16} />
            {mode === 'create' ? 'Create' : 'Save Changes'}
          </button>
        </div>
      </div>
    </>
  );
};

// ============================================================================
// View Modal
// ============================================================================

export const BusinessUnitViewModal: React.FC<BusinessUnitViewModalProps> = ({
  isOpen,
  record,
  onClose,
}) => {
  if (!isOpen || !record) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        <div className="modal-header">
          <h2 className="modal-title">Branch Details</h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <div>
              <label className="form-label">Entity (Company)</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {record.entityName}
              </div>
            </div>
            <div>
              <label className="form-label">BU Code</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {record.buCode}
              </div>
            </div>
            <div>
              <label className="form-label">BU Name</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {record.buName}
              </div>
            </div>
            <div>
              <label className="form-label">BU Type</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {{ BRANCH: 'Branch', HEAD_OFFICE: 'Head Office', REGIONAL_OFFICE: 'Regional Office' }[record.buType] || record.buType}
              </div>
            </div>
            <div>
              <label className="form-label">Status</label>
              <div style={{ padding: '0.5rem' }}>
                <span style={getStatusBadgeStyle(record.isActive ? 'ACTIVE' : 'INACTIVE')}>
                  {record.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            {record.description && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Description</label>
                <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                  {record.description}
                </div>
              </div>
            )}
            <div>
              <label className="form-label">Created By</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {record.createdByName || '—'}
              </div>
            </div>
            <div>
              <label className="form-label">Created At</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {formatDate(record.createdAt)}
              </div>
            </div>
            {record.updatedAt && (
              <>
                <div>
                  <label className="form-label">Updated By</label>
                  <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                    {record.updatedByName || '—'}
                  </div>
                </div>
                <div>
                  <label className="form-label">Updated At</label>
                  <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                    {formatDate(record.updatedAt)}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </>
  );
};
